import { useState } from "react";
import { Formik, Form } from "formik";
import Card from "@mui/material/Card";
import Grid from "@mui/material/Grid";
import AppBar from "@mui/material/AppBar";
import Tabs from "@mui/material/Tabs";
import Tab from "@mui/material/Tab";

import VuiBox from "components/VuiBox";
import VuiTypography from "components/VuiTypography";
import VuiInput from "components/VuiInput";
import VuiButton from "components/VuiButton";
import VuiPhoneInput from "components/VuiPhoneInput";

import { profileSchema, passwordSchema } from "validations";
import useCommon from "hooks/useCommon";
import { updateProfile } from "store/slice/userSlice";
import VuiAlert from "components/VuiAlert";
import { FaEye, FaEyeSlash, FaUndo } from "react-icons/fa";
import { updatePassword } from "store/slice/userSlice";
import PasswordInput from "components/PaaswordInput";
import VuiTooltip from "components/VuiTooltip";
import { useNavigate } from "react-router-dom";
import { logoutUser } from "store/slice/userSlice";
import ReloginDialog from "../utils/ReLoginDialog";
import { getApiErrorMessage } from "services/getError";

function PlatformSettings({ onNotify }) {
  const { STORE, DISPATCH } = useCommon();
  const { user, updateProfileLoading, updateProfileError, updatePasswordLoading } = STORE(
    (state) => state.user
  );

  const [tab, setTab] = useState(0);
  const [reloginDialogOpen, setReloginDialogOpen] = useState(false);

  const [showPassword, setShowPassword] = useState({
    current: false,
    new: false,
    confirm: false,
  });

  const navigate = useNavigate();

  const roleTitle = user?.role?.title
    ? user.role.title.charAt(0).toUpperCase() + user.role.title.slice(1)
    : "";

  const handleTabChange = (_, newValue) => {
    setTab(newValue);
  };

  const handleReloginConfirm = async () => {
    await DISPATCH(logoutUser());
    navigate("/authentication/sign-in", { replace: true });
  };

  const handleProfileSubmit = (values) => {
    const payload = {
      name: values.fullName,
      contactNumber: values.contactNumber,
    };

    DISPATCH(updateProfile(payload))
      .unwrap()
      .then((res) => {
        if (res?.success) {
          onNotify({
            color: "success",
            title: "Profile Updated",
            content: "Your profile was updated successfully",
            icon: "check",
          });
        }
      })
      .catch((err) => {
        onNotify({
          color: "error",
          title: "Update Failed",
          content: err?.message || "Failed to update profile",
          icon: "warning",
        });
      });
  };

  const handlePasswordSubmit = async (values) => {
    const payload = {
      currentPassword: values.currentPassword,
      password: values.newPassword,
    };

    await DISPATCH(updatePassword(payload))
      .unwrap()
      .then((res) => {
        if (res?.success) {
          setReloginDialogOpen(true);
        }
      })
      .catch((err) => {
        onNotify({
          color: "error",
          title: "Update Failed",
          content: getApiErrorMessage(err, "Failed to update password"),
          icon: "warning",
        });
      });
  };

  return (
    <Card sx={{ minHeight: "390px", height: "100%" }}>
      {/* <VuiTypography variant="lg" fontWeight="bold"  mb={2}>
        Profile Settings
      </VuiTypography> */}

      {/* ================= TABS ================= */}
      <AppBar position="static" color="transparent" elevation={0}>
        <Tabs
          value={tab}
          variant="fullWidth"
          onChange={handleTabChange}
          sx={{
            background: "transparent",
            display: "flex",
            justifyContent: "flex-end",

            "& .MuiTab-root": {
              minWidth: { xs: "auto", md: 350 },
              px: { xs: 2, sm: 3 },
              fontSize: { xs: "0.75rem", sm: "0.875rem" },
              whiteSpace: "nowrap",
            },
          }}
        >
          <Tab label="Profile Settings" />
          <Tab label="Update Password" />
        </Tabs>
      </AppBar>

      {/* ================= PROFILE TAB ================= */}
      {tab === 0 && (
        <VuiBox mt={3}>
          {alert.open && (
            <VuiAlert
              color={alert.color}
              icon={alert.icon}
              dismissible
              onClose={() => setAlert((prev) => ({ ...prev, open: false }))}
              sx={{ fontSize: "14px" }}
            >
              {alert.content}
            </VuiAlert>
          )}

          <Formik
            enableReinitialize
            initialValues={{
              fullName: user?.name,
              contactNumber: user?.contactNumber,
            }}
            validationSchema={profileSchema}
            onSubmit={handleProfileSubmit}
          >
            {({
              values,
              errors,
              touched,
              handleChange,
              setFieldValue,
              setFieldTouched,
              dirty,
              resetForm,
              submitForm,
            }) => (
              <Form>
                <Grid container spacing={2} mb={2}>
                  <Grid item xs={12} md={12}>
                    <VuiTypography variant="caption" ml={1}>
                      Full Name
                    </VuiTypography>
                    <VuiInput
                      textOnly
                      gradient
                      fullWidth
                      name="fullName"
                      value={values.fullName}
                      onChange={handleChange}
                      error={touched.fullName && !!errors.fullName}
                      inputProps={{ maxLength: 30 }}
                    />
                    {touched.fullName && errors.fullName && (
                      <VuiTypography variant="caption" color="error" ml={1}>
                        {errors.fullName}
                      </VuiTypography>
                    )}
                  </Grid>
                </Grid>

                <VuiBox mb={2}>
                  {/* <VuiPhoneInput
                    label="Contact Number"
                    value={values.contactNumber}
                    defaultCountry="US"
                    onChange={(value) => {
                      setFieldValue("contactNumber", value || "");
                      setFieldTouched("contactNumber", true);
                      handleChange({
                        target: {
                          name: "contactNumber",
                          value,
                        },
                      });
                    }}
                    error={touched.contactNumber && errors.contactNumber}
                  /> */}
                  <VuiPhoneInput
                    label="Contact Number"
                    value={values.contactNumber}
                    defaultCountry="US"
                    onChange={(value) => {
                      setFieldValue("contactNumber", value || "");
                    }}
                    onBlur={() => setFieldTouched("contactNumber", true)}
                    error={touched.contactNumber && errors.contactNumber}
                  />
                </VuiBox>

                <VuiBox mb={3} ml={1}>
                  <VuiTypography variant="caption">Role:</VuiTypography>
                  <VuiTypography variant="button" fontWeight="medium" ml={1}>
                    {roleTitle}
                  </VuiTypography>
                </VuiBox>

                <VuiBox display="flex" justifyContent="flex-end" gap={1.5}>
                  <VuiTooltip title="Reset Values" placement="top">
                    <VuiButton
                      variant="outlined"
                      color="primary"
                      onClick={() => resetForm()}
                      disabled={!dirty}
                    >
                      {/* <FaUndo size={12} /> */}
                      Revert
                    </VuiButton>
                  </VuiTooltip>

                  <VuiButton
                    type="submit"
                    color="success"
                    variant="gradient"
                    onClick={submitForm}
                    disabled={updateProfileLoading || !dirty}
                  >
                    {updateProfileLoading ? "Saving..." : "Save Changes"}
                  </VuiButton>
                </VuiBox>
              </Form>
            )}
          </Formik>
        </VuiBox>
      )}

      {/* ================= PASSWORD TAB ================= */}
      {tab === 1 && (
        <VuiBox mt={3}>
          {alert.open && (
            <VuiAlert
              color={alert.color}
              icon={alert.icon}
              dismissible
              onClose={() => setAlert((prev) => ({ ...prev, open: false }))}
              sx={{ fontSize: "14px" }}
            >
              {alert.content}
            </VuiAlert>
          )}
          <Formik
            initialValues={{
              currentPassword: "",
              newPassword: "",
              confirmPassword: "",
            }}
            validationSchema={passwordSchema}
            onSubmit={handlePasswordSubmit}
            // onSubmit={(values, { resetForm }) => handlePasswordSubmit(values, resetForm)}
          >
            {({ values, errors, touched, handleChange, submitForm, resetForm }) => (
              <Form>
                <VuiBox mb={2}>
                  <VuiTypography variant="caption" ml={1}>
                    Current Password
                  </VuiTypography>
                  <PasswordInput
                    name="currentPassword"
                    value={values.currentPassword}
                    onChange={handleChange}
                    error={touched.currentPassword && !!errors.currentPassword}
                  />

                  {touched.currentPassword && errors.currentPassword && (
                    <VuiTypography variant="caption" color="error" ml={1}>
                      {errors.currentPassword}
                    </VuiTypography>
                  )}
                </VuiBox>

                <Grid container spacing={2}>
                  <Grid item xs={12} md={6}>
                    <VuiTypography variant="caption" ml={1}>
                      New Password
                    </VuiTypography>
                    <PasswordInput
                      name="newPassword"
                      value={values.newPassword}
                      onChange={handleChange}
                      error={touched.newPassword && !!errors.newPassword}
                    />

                    {touched.newPassword && errors.newPassword && (
                      <VuiTypography variant="caption" color="error" ml={1}>
                        {errors.newPassword}
                      </VuiTypography>
                    )}
                  </Grid>

                  <Grid item xs={12} md={6}>
                    <VuiTypography variant="caption" ml={1}>
                      Confirm Password
                    </VuiTypography>
                    <PasswordInput
                      name="confirmPassword"
                      value={values.confirmPassword}
                      onChange={handleChange}
                      error={touched.confirmPassword && !!errors.confirmPassword}
                    />

                    {touched.confirmPassword && errors.confirmPassword && (
                      <VuiTypography variant="caption" color="error" ml={1}>
                        {errors.confirmPassword}
                      </VuiTypography>
                    )}
                  </Grid>
                </Grid>

                <VuiBox display="flex" justifyContent="flex-end" mt={4}>
                  <VuiButton
                    type="submit"
                    color="success"
                    variant="gradient"
                    disabled={updatePasswordLoading}
                  >
                    {updatePasswordLoading ? "Updating..." : "Update Password"}
                  </VuiButton>
                </VuiBox>
              </Form>
            )}
          </Formik>
        </VuiBox>
      )}

      <ReloginDialog open={reloginDialogOpen} onConfirm={handleReloginConfirm} />
    </Card>
  );
}

export default PlatformSettings;
